import React, { useEffect, useState } from 'react';

import { MenuItem, Select, TableCell, TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import classes from './RiskSelectRow.module.scss';
import type { BransjeRisiko } from '@klp/formgenerator/services/rtkApi';
import { usePostBransjeRisikoMutation } from '@klp/leverandor/services';
import { showToast } from '@klpkonsern/ui';
import { useCurrentUser } from '@klp/auth';

interface RiskSelectRowProps {
    row: BransjeRisiko;
}

interface RiskPayload {
    bransjeId: string;
    risiko: number;
}

export const RiskSelectRow = ({ row }: RiskSelectRowProps) => {
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const readOnly = !currentUser?.isEDAdmin;
    const [selectedValue, setSelectedValue] = useState<number | ''>(
        row.risiko ?? '',
    );
    const [postBransjeRisiko, { isSuccess, isError }] =
        usePostBransjeRisikoMutation();

    useEffect(() => {
        if (isSuccess) {
            showToast({
                body: t('leverandor.inherentRisk.success'),
                type: 'success',
            });
        } else if (isError) {
            showToast({
                body: t('leverandor.inherentRisk.error'),
                type: 'error',
            });
            setSelectedValue(row.risiko ?? '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, isError]);

    const riskOptions = [
        t('leverandor.inherentRisk.low'),
        t('leverandor.inherentRisk.medium'),
        t('leverandor.inherentRisk.high'),
    ];

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newValue = event.target.value as number;
        setSelectedValue(newValue);
        const updatedRisk: RiskPayload = {
            bransjeId: row.id.toString(),
            risiko: newValue,
        };
        postBransjeRisiko(updatedRisk);
    };

    return (
        <TableRow key={row.navn}>
            <TableCell scope="row">{row.navn}</TableCell>
            <TableCell scope="row">{row.beskrivelse}</TableCell>
            <TableCell className={classes.riskColumn}>
                <Select
                    value={selectedValue}
                    readOnly={readOnly}
                    disabled={readOnly}
                    onChange={handleChange}
                    displayEmpty={true}
                    renderValue={(value) => {
                        if (value === '') {
                            return t('leverandor.inherentRisk.notSet');
                        }
                        return riskOptions[(value as number) - 1];
                    }}
                >
                    {riskOptions.map((option, index) => (
                        <MenuItem key={option} value={index + 1}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </TableCell>
        </TableRow>
    );
};
